import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Trash2, Calculator, AlertCircle } from 'lucide-react';
import { formatHours, formatCurrency } from '@/lib/budget-utils';
import type { ResourceBudget, RoleBudget, PersonBudget, PhaseBudgetResource } from '@/types/budget.types';

interface EditResourceBudgetFormProps {
  resourceBudget: ResourceBudget;
  onChange: (changes: ResourceBudget) => void;
}

// Predefined role options
const ROLE_OPTIONS = [
  'Project Manager',
  'Senior Consultant',
  'Consultant',
  'Developer',
  'Senior Developer',
  'QA Analyst',
  'Business Analyst',
  'Technical Architect',
  'UX Designer',
  'Scrum Master',
];

export function EditResourceBudgetForm({
  resourceBudget,
  onChange,
}: EditResourceBudgetFormProps) {
  const [byRole, setByRole] = useState<RoleBudget[]>(resourceBudget?.byRole || []);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [deleteAttempt, setDeleteAttempt] = useState<{ index: number; role: RoleBudget } | null>(null);
  const [byPerson, setByPerson] = useState<PersonBudget[]>(resourceBudget?.byPerson || []);
  const [byPhase, setByPhase] = useState<PhaseBudgetResource[]>(resourceBudget?.byPhase || []);

  useEffect(() => {
    const summary = calculateSummary(byRole, byPerson);
    onChange({
      ...resourceBudget,
      byRole,
      byPerson,
      byPhase,
      summary,
    });
  }, [byRole, byPerson, byPhase]);

  const calculateSummary = (roles: RoleBudget[], people: PersonBudget[]) => {
    const totalBudget = roles.reduce((sum, role) => sum + (role.budgetHours || 0), 0);
    const totalActual = roles.reduce((sum, role) => sum + (role.actualHours || 0), 0);
    const totalRemaining = totalBudget - totalActual;
    const progressPercent = totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0;

    const totalBudgetCost = roles.reduce((sum, role) => sum + (role.budgetCost || 0), 0);
    const totalActualCost = roles.reduce((sum, role) => sum + (role.actualCost || 0), 0);

    // Calculate billable percentage (assuming all hours are billable for now)
    const billablePercent = totalActual > 0 ? 85 : 0; // Default assumption

    return {
      totalBudget,
      totalActual,
      totalRemaining,
      progressPercent,
      billablePercent,
      targetBillablePercent: 80,
      totalBudgetCost,
      totalActualCost,
    };
  };

  // Role handlers
  const addRole = () => {
    const newRole: RoleBudget = {
      id: `role-${Date.now()}`,
      role: ROLE_OPTIONS[0], // Default to first role
      budgetHours: 0,
      actualHours: 0,
      remainingHours: 0,
      percentUsed: 0,
      billRate: 150, // Default rate
      budgetCost: 0,
      actualCost: 0,
    };
    setByRole([...byRole, newRole]);
  };

  const updateRole = (index: number, field: keyof RoleBudget, value: any) => {
    // Prevent modification of billRate field - it should be read-only
    if (field === 'billRate') {
      console.warn('billRate field is read-only and cannot be modified');
      return;
    }
    
    const updated = [...byRole];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    
    // Recalculate derived fields
    if (field === 'budgetHours' || field === 'actualHours') {
      const budgetHours = field === 'budgetHours' ? value : updated[index].budgetHours;
      const actualHours = field === 'actualHours' ? value : updated[index].actualHours;
      const billRate = updated[index].billRate; // Always use existing billRate
      
      updated[index].remainingHours = budgetHours - actualHours;
      updated[index].percentUsed = budgetHours > 0 ? (actualHours / budgetHours) * 100 : 0;
      updated[index].budgetCost = budgetHours * billRate;
      updated[index].actualCost = actualHours * billRate;
    }
    
    setByRole(updated);
  };

  const removeRole = (index: number) => {
    const role = byRole[index];
    
    // Validation: Check if role has logged hours
    if (role.actualHours > 0) {
      // Set validation error and show dialog
      setDeleteAttempt({ index, role });
      setValidationError(
        `Cannot remove "${role.role}" because it has ${formatHours(role.actualHours)} of logged time. ` +
        `Please reassign or remove the logged hours before removing this role.`
      );
      return;
    }
    
    // If no logged hours, allow removal
    setByRole(byRole.filter((_, i) => i !== index));
    setValidationError(null);
  };
  
  const confirmDeleteRole = () => {
    // This should never be called if validation passes, but included for safety
    if (deleteAttempt && deleteAttempt.role.actualHours === 0) {
      setByRole(byRole.filter((_, i) => i !== deleteAttempt.index));
    }
    setDeleteAttempt(null);
    setValidationError(null);
  };
  
  const cancelDeleteRole = () => {
    setDeleteAttempt(null);
    setValidationError(null);
  };

  // Person handlers
  const addPerson = () => {
    const newPerson: PersonBudget = {
      id: `person-${Date.now()}`,
      personId: `p-${Date.now()}`,
      personName: 'Unassigned',
      role: 'Select Role',
      budgetHours: 0,
      actualHours: 0,
      utilisation: 0,
      costRate: 0,
      budgetCost: 0,
      actualCost: 0,
    };
    setByPerson([...byPerson, newPerson]);
  };

  const updatePerson = (index: number, field: keyof PersonBudget, value: any) => {
    const updated = [...byPerson];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    
    // Recalculate derived fields
    if (field === 'budgetHours' || field === 'actualHours' || field === 'costRate') {
      const budgetHours = field === 'budgetHours' ? value : updated[index].budgetHours;
      const actualHours = field === 'actualHours' ? value : updated[index].actualHours;
      const costRate = field === 'costRate' ? value : updated[index].costRate;
      
      updated[index].utilisation = budgetHours > 0 ? (actualHours / budgetHours) * 100 : 0;
      updated[index].budgetCost = budgetHours * costRate;
      updated[index].actualCost = actualHours * costRate;
    }
    
    setByPerson(updated);
  };

  const removePerson = (index: number) => {
    setByPerson(byPerson.filter((_, i) => i !== index));
  };

  // Phase handlers
  const addPhase = () => {
    const newPhase: PhaseBudgetResource = {
      phaseId: `phase-${Date.now()}`,
      phaseName: 'New Phase',
      budgetHours: 0,
      actualHours: 0,
      remainingHours: 0,
      status: 'not-started',
    };
    setByPhase([...byPhase, newPhase]);
  };

  const updatePhase = (index: number, field: keyof PhaseBudgetResource, value: any) => {
    const updated = [...byPhase];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    
    // Recalculate remaining hours
    if (field === 'budgetHours' || field === 'actualHours') {
      const budgetHours = field === 'budgetHours' ? value : updated[index].budgetHours;
      const actualHours = field === 'actualHours' ? value : updated[index].actualHours;
      updated[index].remainingHours = Math.max(0, budgetHours - actualHours);
    }
    
    setByPhase(updated);
  };

  const removePhase = (index: number) => {
    setByPhase(byPhase.filter((_, i) => i !== index));
  };

  const summary = calculateSummary(byRole, byPerson);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            Resource Budget Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <Label className="text-muted-foreground">Total Budget</Label>
              <div className="text-2xl font-bold">{formatHours(summary.totalBudget)}</div>
              <div className="text-xs text-muted-foreground">{formatCurrency(summary.totalBudgetCost)}</div>
            </div>
            <div>
              <Label className="text-muted-foreground">Actual</Label>
              <div className="text-2xl font-bold text-blue-600">
                {formatHours(summary.totalActual)}
              </div>
              <div className="text-xs text-muted-foreground">{formatCurrency(summary.totalActualCost)}</div>
            </div>
            <div>
              <Label className="text-muted-foreground">Remaining</Label>
              <div className="text-2xl font-bold text-green-600">
                {formatHours(summary.totalRemaining)}
              </div>
              <div className="text-xs text-muted-foreground">
                {formatCurrency(summary.totalBudgetCost - summary.totalActualCost)}
              </div>
            </div>
            <div>
              <Label className="text-muted-foreground">Progress</Label>
              <div className="text-2xl font-bold">
                {summary.progressPercent.toFixed(1)}%
              </div>
              <div className="text-xs text-muted-foreground">
                Billable: {summary.billablePercent.toFixed(0)}%
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Validation Error Alert */}
      {validationError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Cannot Remove Role</AlertTitle>
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteAttempt !== null} onOpenChange={(open) => !open && cancelDeleteRole()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot Remove Role with Logged Hours</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                The role <strong>"{deleteAttempt?.role.role}"</strong> cannot be removed because it has logged hours associated with it.
              </p>
              <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Logged Hours:</span>
                  <span className="font-semibold">{deleteAttempt && formatHours(deleteAttempt.role.actualHours)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Actual Cost:</span>
                  <span className="font-semibold">{deleteAttempt && formatCurrency(deleteAttempt.role.actualCost)}</span>
                </div>
              </div>
              <p className="text-sm">
                <strong>To remove this role, you must:</strong>
              </p>
              <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                <li>Reassign the logged hours to another role, or</li>
                <li>Remove the logged time entries from the time tracking system</li>
              </ul>
              <p className="text-sm text-muted-foreground">
                This ensures data integrity between resource budgets and time tracking.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={cancelDeleteRole}>
              I Understand
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Hours by Role</CardTitle>
          <Button onClick={addRole} size="sm" variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Add Role
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {byRole.map((role, index) => (
            <div key={role.id} className="border rounded-lg p-4">
              <div className="grid grid-cols-7 gap-3">
                <div className="col-span-2">
                  <Label>Role</Label>
                  <select
                    value={role.role}
                    onChange={(e) => updateRole(index, 'role', e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    {ROLE_OPTIONS.map((roleOption) => (
                      <option key={roleOption} value={roleOption}>
                        {roleOption}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label>Budget (hrs)</Label>
                  <Input
                    type="number"
                    value={role.budgetHours}
                    onChange={(e) =>
                      updateRole(index, 'budgetHours', parseFloat(e.target.value) || 0)
                    }
                    placeholder="0"
                    min="0"
                    step="0.5"
                  />
                </div>
                
                <div>
                  <Label className="text-muted-foreground">Rate (£/hr)</Label>
                  <div className="h-10 flex items-center px-3 py-2 text-sm font-medium bg-muted rounded-md border border-input">
                    £{role.billRate.toFixed(2)}
                  </div>
                </div>
                
                <div>
                  <Label className="text-muted-foreground">Budget Cost</Label>
                  <div className="h-10 flex items-center text-sm font-medium">
                    {formatCurrency(role.budgetCost || 0)}
                  </div>
                </div>
                
                <div>
                  <Label className="text-muted-foreground">Actual (hrs)</Label>
                  <div className="h-10 flex items-center text-sm">
                    {formatHours(role.actualHours || 0)}
                  </div>
                </div>
                
                <div className="flex items-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeRole(index)}
                    disabled={role.actualHours > 0}
                    title={
                      role.actualHours > 0
                        ? `Cannot delete: ${formatHours(role.actualHours)} logged`
                        : 'Remove role'
                    }
                  >
                    <Trash2 
                      className={`h-4 w-4 ${
                        role.actualHours > 0 
                          ? 'text-muted-foreground' 
                          : 'text-destructive'
                      }`} 
                    />
                  </Button>
                </div>
              </div>
            </div>
          ))}

          {byRole.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No roles yet. Click "Add Role" to get started.
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
